import { createMemo, For, Show, type JSX } from "solid-js";
import { formatOptionValue, layoutPrint, type PointTuple, type PrintRect, MIN_SHEET_COVERAGE, printBounds, printCoverage, PRINT_GRID, printShapes, printSheet, type DraftBlock, type PaperSize, type PrintSheet, type DraftResult, type DraftSegment, type OptionDefinition, type OptionValue, type PatternProject, type PatternSpecification } from "@tailor-frame/engine";
import { pieceLetter, piecePrintColour } from "../core/pieceColours";

/**
 * Rainbow alignment lines run up and across the whole tiled drawing at a fixed
 * spacing, so each line continues from one sheet onto its neighbour in the same
 * colour. Offset by half a spacing so they never sit on a sheet edge.
 */
const ALIGNMENT_SPACING = 40;
const ALIGNMENT_OFFSET = ALIGNMENT_SPACING / 2;
const RAINBOW = ["#e53935", "#fb8c00", "#f2b705", "#43a047", "#1e88e5", "#3949ab", "#8e24aa"];
const alignmentColour = (index: number) => RAINBOW[((index % RAINBOW.length) + RAINBOW.length) % RAINBOW.length];
/** Vertical lines are numbered, horizontal lines lettered, the same on every sheet. */
const alignmentLetter = (index: number) => {
  let label = "";
  for (let n = index; n >= 0; n = Math.floor(n / 26) - 1) label = String.fromCharCode(65 + (n % 26)) + label;
  return label;
};

/** Alignment line positions (in drawing-grid mm) that fall within `start`..`start + length`. */
function alignmentLines(start: number, length: number): Array<{ index: number; at: number }> {
  const first = Math.ceil((start - ALIGNMENT_OFFSET) / ALIGNMENT_SPACING);
  const last = Math.floor((start + length - ALIGNMENT_OFFSET) / ALIGNMENT_SPACING);
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => ({ index: first + i, at: ALIGNMENT_OFFSET + (first + i) * ALIGNMENT_SPACING }));
}

/** Side of the calibration square on the cover sheet, in millimetres. */
const CALIBRATION_SIZE = 50;

interface Tile {
  column: number;
  row: number;
  index: number;
  originX: number;
  originY: number;
  /** Sheet number as printed, or undefined for a blank sheet that is skipped. */
  sheet?: number;
}

interface DrawingBounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

/** One garment to print: its draft and what it was drafted from. */
export interface PrintGarment {
  draft: DraftResult;
  specification: PatternSpecification;
  project: PatternProject;
  /** Pieces turned off in the workspace: left out of the layout, the sheets and the bill of parts. */
  excluded: string[];
  /** In a print set, the letter the garment's pieces are prefixed by (F‑A, T‑B…), and its short name. */
  code?: string;
  name?: string;
}

/** A piece placed on a chapter's drawing: how it is turned and moved there, and what it covers. */
export interface ChapterPiece {
  block: DraftBlock;
  draft: DraftResult;
  /** Its letter and colour within its garment. */
  index: number;
  code?: string;
  /** SVG transform from the piece's own coordinates onto the drawing. */
  transform: string;
  /** Laid face down: its labels are flipped back so they still read. */
  mirrored?: boolean;
  rect: PrintRect;
  shapes: PointTuple[][];
}

/** The pieces of a chapter on its drawing, and the sheets that drawing is cut into. */
export interface Chapter {
  /** Whose draft heads the chapter: pattern name, made for, printed date. */
  draft: DraftResult;
  code?: string;
  name?: string;
  pieces: ChapterPiece[];
  /** Area the drawing covers beyond its pieces, such as the cloth they are laid on. */
  extent?: PrintRect;
  /** Drawn under the pieces in drawing coordinates (the cloth's fold and selvedges); `scale` is drawing mm per printed mm. */
  backdrop?: (scale: number) => JSX.Element;
  bounds: DrawingBounds;
  columns: number;
  rows: number;
  tiles: Tile[];
  printed: Tile[];
  parts: Part[];
}

/** A garment laid out for the chosen paper: a chapter, with the pieces turned off left out. */
interface GarmentChapter extends Chapter, PrintGarment { leftOut: DraftBlock[] }

/** A piece's letter as printed: prefixed by its garment's code in a print set. */
const partLetter = (index: number, code?: string) => code ? `${code}-${pieceLetter(index)}` : pieceLetter(index);
/** What a chapter's sheets and pieces are headed with. */
const chapterTitle = (chapter: Pick<PrintGarment, "code" | "name" | "draft">) => chapter.code ? `${chapter.code} · ${chapter.name ?? chapter.draft.patternName}` : chapter.name ?? chapter.draft.specificationId;

function buildChapter(garment: PrintGarment, sheet: PrintSheet): GarmentChapter {
  // Laid out on copies, so the draft itself is left as it was.
  const blocks = garment.draft.blocks.filter((block) => !garment.excluded.includes(block.id)).map((block) => ({ ...block }));
  layoutPrint(blocks, sheet);
  const laidOut = { ...garment.draft, blocks };
  const pieces = blocks.map((block, index): ChapterPiece => ({ block, draft: laidOut, index, code: garment.code, transform: printTransform(block), rect: printBounds(block), shapes: printShapes(block, laidOut) }));
  return { ...garment, ...tileChapter({ draft: garment.draft, code: garment.code, name: garment.name, pieces }, sheet), leftOut: garment.draft.blocks.filter((block) => garment.excluded.includes(block.id)) };
}

/** Cuts a chapter's drawing into numbered sheets, leaving out those with next to nothing on them. */
export function tileChapter(base: Pick<Chapter, "draft" | "code" | "name" | "pieces" | "extent" | "backdrop">, sheet: PrintSheet): Chapter {
  const { pieces } = base;
  const rects = [...pieces.map((piece) => piece.rect), ...(base.extent ? [base.extent] : [])];
  const bounds = rects.length === 0 ? { minX: 0, minY: 0, width: 0, height: 0 } : (() => {
    const minX = Math.min(...rects.map((rect) => rect.x));
    const minY = Math.min(...rects.map((rect) => rect.y));
    return { minX, minY, width: Math.max(...rects.map((rect) => rect.x + rect.width)) - minX, height: Math.max(...rects.map((rect) => rect.y + rect.height)) - minY };
  })();
  const { width, height, stepX, stepY, overlap } = sheet;
  const columns = Math.max(1, Math.ceil(Math.max(0, bounds.width - overlap) / stepX));
  const rows = Math.max(1, Math.ceil(Math.max(0, bounds.height - overlap) / stepY));

  // Each sheet owns the part of the drawing its right and lower neighbours do
  // not also print (the whole sheet for the last column and row). Sheets whose
  // pieces cover under MIN_SHEET_COVERAGE of what they own are left out — a
  // corner of a bounding box or the tip of a piece is not worth a sheet; a
  // piece reaching only into an overlap is already whole on the sheet before.
  // Each piece's best-covered sheet always prints, so no piece goes missing.
  // The rest are numbered in reading order, and the cover map shows where each goes.
  const cells = Array.from({ length: columns * rows }, (_, index) => {
    const row = Math.floor(index / columns);
    const column = index % columns;
    const originX = column * stepX;
    const originY = row * stepY;
    const owned = { x: bounds.minX + originX, y: bounds.minY + originY, width: column === columns - 1 ? width : stepX, height: row === rows - 1 ? height : stepY };
    return { column, row, index, originX, originY, coverage: pieces.map((piece) => printCoverage(piece.shapes, owned)) };
  });
  const homes = new Set(pieces.map((_, piece) => cells.reduce((best, cell) => (cell.coverage[piece] > best.coverage[piece] ? cell : best)).index));
  let number = 0;
  const tiles = cells.map(({ coverage, ...cell }): Tile => {
    const used = homes.has(cell.index) || coverage.reduce((sum, share) => sum + share, 0) >= MIN_SHEET_COVERAGE;
    return { ...cell, sheet: used ? ++number : undefined };
  });
  const printed = tiles.filter((tile) => tile.sheet !== undefined);

  // Every printed sheet a piece reaches onto, so the bill of parts says where to find it.
  const parts = pieces.map((piece): Part => {
    const rect = piece.rect;
    const sheets = printed.filter((tile) => {
      const x0 = bounds.minX + tile.originX;
      const y0 = bounds.minY + tile.originY;
      return rect.x < x0 + width && rect.x + rect.width > x0 && rect.y < y0 + height && rect.y + rect.height > y0;
    }).map((tile) => tile.sheet as number);
    return { index: piece.index, block: piece.block, sheets, code: piece.code };
  });
  return { ...base, bounds, columns, rows, tiles, printed, parts };
}

/**
 * The tiled print of one pattern or of a print set. A single pattern prints as
 * it always has: cover with its assembly map, configuration, sheets. A set
 * prints one cover with the calibration square and contents, one configuration
 * for every garment, then each garment as a chapter — its own assembly map and
 * its own numbered sheets, so one garment's sheets tape together on their own.
 */
export function PrintDocument(props: { garments: PrintGarment[]; title?: string; paper: PaperSize }) {
  const paper = () => props.paper;
  const sheet = createMemo(() => printSheet(paper()));
  const chapters = createMemo(() => props.garments.map((garment) => buildChapter(garment, sheet())));
  const isSet = () => props.garments.length > 1 || props.garments.some((garment) => garment.code);
  const printedCount = () => chapters().reduce((total, chapter) => total + chapter.printed.length, 0);
  const skippedCount = () => chapters().reduce((total, chapter) => total + chapter.tiles.length - chapter.printed.length, 0);
  const first = () => props.garments[0].draft;
  const configurationSheets = () => paginateConfiguration(chapters().flatMap((chapter) => configurationLines(chapter.specification, chapter.project, chapter.parts, isSet() ? chapter.name ?? chapter.draft.patternName : undefined)), configurationRows(sheet()));
  const title = () => props.title ?? first().patternName;

  return (
    <section class="print-page">
      <p class="print-warning no-print">{paper()}: cover + {configurationSheets().length} configuration + {isSet() ? `${chapters().length} assembly map + ` : ""}{printedCount()} pattern sheet(s) · {skippedCount()} near-blank skipped · {sheet().width} × {sheet().height} mm printable. Load {paper()} paper, print at 100% / “Actual size”, disable browser headers and footers, and check the 50 mm square on the cover before printing the rest. Overlap adjacent sheets by {sheet().overlap} mm: lay each sheet's top-left edge on its neighbour's dashed overlap line and match the grid and registration crosses.</p>
      <style>{`@page { size: ${paper()} portrait; margin: 0; }`}</style>
      {/* Keyed: every sheet is drawn afresh for a new paper size. */}
      <Show when={sheet()} keyed>{(size) => <div class="paper-stack" style={{ "--paper-width": `${size.pageWidth}mm`, "--paper-height": `${size.pageHeight}mm` }}>
        <Show when={isSet()} fallback={<PrintCover sheet={size} chapter={chapters()[0]} calibration />}>
          <PrintSetCover sheet={size} title={title()} chapters={chapters()} />
        </Show>
        <For each={configurationSheets()}>{(lines, index) => <PrintConfiguration sheet={size} draft={first()} title={title()} lines={lines} page={index() + 1} total={configurationSheets().length} />}</For>
        <For each={chapters()}>{(chapter) => <>
          <Show when={isSet()}><PrintCover sheet={size} chapter={chapter} calibration={false} /></Show>
          <For each={chapter.printed}>{(tile) => <PrintTile sheet={size} chapter={chapter} tile={tile} />}</For>
        </>}</For>
      </div>}</Show>
    </section>
  );
}

/** A print set's first page: the calibration square, how to assemble, and which garments follow on how many sheets. */
function PrintSetCover(props: { sheet: PrintSheet; title: string; chapters: Chapter[] }) {
  const sheet = props.sheet;
  const draft = () => props.chapters[0].draft;
  return (
    <article class="paper">
      <svg viewBox={`0 0 ${sheet.pageWidth} ${sheet.pageHeight}`} width={`${sheet.pageWidth}mm`} height={`${sheet.pageHeight}mm`} aria-label="Print set cover sheet with calibration square and contents">
        <text x={20} y={24} font-size="7" font-weight="bold">{props.title}</text>
        <text x={20} y={31} font-size="3.5">Made for {draft().madeFor} · Printed {draft().printedDate} · {props.chapters.length} garments</text>
        <Calibration sheet={sheet} />
        <text x={20} y={124} font-size="3.6" font-weight="bold">Contents</text>
        <g font-size="2.7" font-weight="bold" fill="#555"><text x={22} y={131}>Garment</text><text x={110} y={131}>Pattern</text><text x={sheet.pageWidth - 20} y={131} text-anchor="end">Pieces · sheets</text></g>
        <For each={props.chapters}>{(chapter, index) => {
          const y = 137 + index() * 6;
          return <g font-size="3.2">
            <text x={22} y={y} font-weight="bold">{chapter.code} · {chapter.name ?? chapter.draft.patternName}</text>
            <text x={110} y={y}>{truncate(`${chapter.draft.patternName} v${chapter.draft.patternVersion}`, 34)}</text>
            <text x={sheet.pageWidth - 20} y={y} text-anchor="end" font-family="ui-monospace, monospace">{chapter.parts.length} · {chapter.printed.length}</text>
            <line x1={22} y1={y + 1.4} x2={sheet.pageWidth - 20} y2={y + 1.4} stroke="#e4e4e4" stroke-width="0.15" />
          </g>;
        }}</For>
        <g font-size="3.2">
          <text x={20} y={145 + props.chapters.length * 6}>Each garment follows as a chapter: an assembly map, then its sheets numbered from 1.</text>
          <text x={20} y={150 + props.chapters.length * 6}>Pieces carry their garment's letter (for example {props.chapters[0].code}-A), so cut pieces never mix.</text>
        </g>
      </svg>
    </article>
  );
}

/** The 50 mm test square, a 100 mm rule and how to check and assemble the sheets. */
function Calibration(props: { sheet: PrintSheet }) {
  return <>
    <rect x={20} y={42} width={CALIBRATION_SIZE} height={CALIBRATION_SIZE} fill="none" stroke="#111" stroke-width="0.4" />
    <text x={20 + CALIBRATION_SIZE / 2} y={42 + CALIBRATION_SIZE / 2 - 2} text-anchor="middle" font-size="4" font-weight="bold">{CALIBRATION_SIZE} mm</text>
    <text x={20 + CALIBRATION_SIZE / 2} y={42 + CALIBRATION_SIZE / 2 + 4} text-anchor="middle" font-size="2.8">test square</text>
    <path d={`M 20 110 H 120 ${Array.from({ length: 11 }, (_, index) => `M ${20 + index * 10} 110 V ${index % 5 === 0 ? 105 : 107}`).join(" ")}`} fill="none" stroke="#111" stroke-width="0.3" />
    <text x={20} y={114.5} font-size="2.8">0</text>
    <text x={120} y={114.5} font-size="2.8" text-anchor="end">100 mm</text>

    <g font-size="3.4">
      <text x={82} y={46} font-weight="bold">Before printing everything</text>
      <text x={82} y={52}>1. Print this page at 100% / “Actual size”.</text>
      <text x={82} y={57}>2. Measure the square: it must be {CALIBRATION_SIZE} × {CALIBRATION_SIZE} mm.</text>
      <text x={82} y={62}>3. If it is not, fix the print scale and try again.</text>
      <text x={82} y={70} font-weight="bold">Assembling the sheets</text>
      <text x={82} y={76}>Lay sheets out as in the assembly map. Neighbouring</text>
      <text x={82} y={80.6}>sheets overlap by {props.sheet.overlap} mm: lay each sheet's edge on</text>
      <text x={82} y={85.2}>the dashed overlap line, match the grid and crosses,</text>
      <text x={82} y={89.8}>then tape. Near-blank sheets are not printed; their</text>
      <text x={82} y={94.4}>places are left empty (finish any line on them by hand).</text>
      <text x={82} y={99}>The rainbow lines run across every sheet: match each</text>
      <text x={82} y={103.6}>line's colour and number (or letter) on both sheets.</text>
    </g>
  </>;
}

export function PrintTile(props: { sheet: PrintSheet; chapter: Chapter; tile: Tile }) {
  const sheet = props.sheet;
  const total = () => props.chapter.printed.length;
  // Ids are unique across chapters: every chapter has a sheet in row 0, column 0.
  const suffix = `-${props.chapter.code ?? ""}${props.tile.column}-${props.tile.row}`;
  const worldX = sheet.marginX - props.tile.originX - props.chapter.bounds.minX;
  const worldY = sheet.marginY - props.tile.originY - props.chapter.bounds.minY;
  return (
    <article class="paper">
      <svg viewBox={`0 0 ${sheet.pageWidth} ${sheet.pageHeight}`} width={`${sheet.pageWidth}mm`} height={`${sheet.pageHeight}mm`} aria-label={`${sheet.paper} pattern sheet ${props.tile.sheet} of ${total()}`}>
        <defs>
          <clipPath id={`printable${suffix}`}><rect x={sheet.marginX} y={sheet.marginY} width={sheet.width} height={sheet.height} /></clipPath>
          <pattern id={`seam-hatch${suffix}`} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="4" stroke="#777" stroke-width="0.35" />
          </pattern>
          {/* Inlay: hatched the other way and dashed, so it reads apart from the allowance. */}
          <pattern id={`inlay-hatch${suffix}`} width="3" height="3" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
            <line x1="0" y1="0" x2="0" y2="3" stroke="#999" stroke-width="0.3" stroke-dasharray="1 0.6" />
          </pattern>
        </defs>

        <PrintGrid sheet={sheet} originX={props.tile.originX} originY={props.tile.originY} />
        <AlignmentLines sheet={sheet} originX={props.tile.originX} originY={props.tile.originY} />
        <g clip-path={`url(#printable${suffix})`}>
          <g transform={`translate(${worldX} ${worldY})`}>
            {props.chapter.backdrop?.(1)}
            <For each={props.chapter.pieces}>{(piece) => <PrintPiece piece={piece} hatchId={`seam-hatch${suffix}`} inlayHatchId={`inlay-hatch${suffix}`} />}</For>
          </g>
        </g>

        <rect x={sheet.marginX} y={sheet.marginY} width={sheet.width} height={sheet.height} fill="none" stroke="#555" stroke-width="0.25" />
        <OverlapLines sheet={sheet} />
        <RegistrationMarks sheet={sheet} />
        {/* Sheet label in the centre of the page; the white halo keeps it legible over pattern lines. */}
        <text x={sheet.pageWidth / 2} y={sheet.pageHeight / 2} text-anchor="middle" dominant-baseline="middle" font-size="4" fill="#111" stroke="#fff" stroke-width="1.2" paint-order="stroke" stroke-linejoin="round">{chapterTitle(props.chapter)} · sheet {props.tile.sheet}/{total()} · row {props.tile.row + 1}, column {props.tile.column + 1}</text>
      </svg>
    </article>
  );
}

/**
 * A pattern's cover: a calibration square to check before printing everything
 * (left off a print set's chapters, whose set cover has it), how to assemble
 * the sheets, a map of where each numbered sheet goes, and the pieces.
 */
export function PrintCover(props: { sheet: PrintSheet; chapter: Chapter; calibration: boolean; title?: string; subtitle?: string }) {
  const sheet = props.sheet;
  const draft = () => props.chapter.draft;
  // A long list (a whole lay of cloth) is left to the map's badges.
  const listed = () => props.chapter.pieces.length <= 12;
  const tiles = () => props.chapter.tiles;
  const bounds = () => props.chapter.bounds;
  const columns = () => props.chapter.columns;
  const rows = () => props.chapter.rows;
  // Without the calibration block, the pieces and the map move up the page.
  const lift = props.calibration ? 0 : 80;
  const mapLeft = 20;
  const mapTop = 150 - lift;
  const mapWidth = sheet.pageWidth - mapLeft * 2;
  const mapHeight = sheet.pageHeight - mapTop - 20;
  // Functions, not values: the grid changes as the draft does.
  const fullWidth = () => columns() * sheet.stepX + sheet.overlap;
  const fullHeight = () => rows() * sheet.stepY + sheet.overlap;
  const mapScale = () => Math.min(mapWidth / fullWidth(), mapHeight / fullHeight());
  // Labels shrink with the sheets on a long map (a whole lay of cloth), so neighbours do not run together.
  const mapText = () => Math.min(4, sheet.stepX * mapScale() * 0.3) / mapScale();
  const badgeRadius = () => Math.min(2.6, sheet.stepX * mapScale() * 0.2) / mapScale();
  return (
    <article class="paper">
      <svg viewBox={`0 0 ${sheet.pageWidth} ${sheet.pageHeight}`} width={`${sheet.pageWidth}mm`} height={`${sheet.pageHeight}mm`} aria-label="Pattern cover sheet with calibration square and assembly map">
        <text x={20} y={24} font-size="7" font-weight="bold">{props.title ?? (props.chapter.code ? `${props.chapter.code} · ${props.chapter.name ?? draft().patternName}` : draft().patternName)}</text>
        <text x={20} y={31} font-size="3.5">{props.subtitle ?? `${props.chapter.code ? `${draft().patternName} · ` : ""}Made for ${draft().madeFor} · Pattern v${draft().patternVersion} · Printed ${draft().printedDate}${props.chapter.code ? ` · ${props.chapter.printed.length} sheets, numbered from 1` : ""}`}</text>
        <Show when={props.calibration}><Calibration sheet={sheet} /></Show>

        <Show when={listed()}>
          <text x={20} y={120 - lift} font-size="3.6" font-weight="bold">Pieces <tspan font-weight="normal" font-size="3">(full bill of parts on the configuration sheet)</tspan></text>
          <For each={props.chapter.pieces}>{(piece, index) => {
            const x = 20 + Math.floor(index() / 4) * 57;
            const y = 126 - lift + (index() % 4) * 5;
            return <g><PieceBadge index={piece.index} code={piece.code} x={x + 1.8} y={y - 1.1} radius={1.9} /><text x={x + 5} y={y} font-size="3">{truncate(piece.block.label, piece.code ? 23 : 26)} ×{piece.block.cutQuantity}</text></g>;
          }}</For>
        </Show>

        <text x={mapLeft} y={mapTop - 3} font-size="3.6" font-weight="bold">Assembly map</text>
        <g transform={`translate(${mapLeft} ${mapTop}) scale(${mapScale()})`}>
          <For each={tiles()}>{(tile) => <rect x={tile.originX} y={tile.originY} width={sheet.width} height={sheet.height} fill={tile.sheet ? "none" : "#f1f1f1"} stroke="#999" stroke-width={0.25 / mapScale()} />}</For>
          <g transform={`translate(${-bounds().minX} ${-bounds().minY})`}>
            {props.chapter.backdrop?.(1 / mapScale())}
            <For each={props.chapter.pieces}>{(piece) => <g transform={piece.transform}>
              <For each={piece.draft.edges.filter((edge) => piece.block.edgeIds.includes(edge.id))}>{(edge) => <path d={edge.segments.map(segmentPath).join(" ")} fill="none" stroke={piecePrintColour(piece.index)} stroke-width={0.35 / mapScale()} />}</For>
            </g>}</For>
            <For each={props.chapter.pieces}>{(piece) => <PieceBadge index={piece.index} code={piece.code} x={piece.rect.x + piece.rect.width / 2} y={piece.rect.y + piece.rect.height * 0.3} radius={badgeRadius()} />}</For>
          </g>
          <For each={alignmentLines(0, fullWidth())}>{(line) => <line x1={line.at} y1={0} x2={line.at} y2={fullHeight()} stroke={alignmentColour(line.index)} stroke-width={0.2 / mapScale()} opacity="0.35" />}</For>
          <For each={alignmentLines(0, fullHeight())}>{(line) => <line x1={0} y1={line.at} x2={fullWidth()} y2={line.at} stroke={alignmentColour(line.index + 3)} stroke-width={0.2 / mapScale()} opacity="0.35" />}</For>
          <For each={tiles()}>{(tile) => <Show when={tile.sheet}>{(number) => <text x={tile.originX + sheet.width / 2} y={tile.originY + sheet.height / 2} text-anchor="middle" dominant-baseline="middle" font-size={String(mapText())} font-weight="bold" fill="#111" stroke="#fff" stroke-width={mapText() / 4} paint-order="stroke">{number()}</text>}</Show>}</For>
        </g>
      </svg>
    </article>
  );
}

interface Part {
  index: number;
  block: DraftBlock;
  /** Printed sheet numbers the piece appears on. */
  sheets: number[];
  /** The garment's letter, in a print set. */
  code?: string;
}

type ConfigurationLine = { kind: "heading"; text: string } | { kind: "value"; label: string; value: string } | { kind: "partsHeader" } | { kind: "part"; part: Part };

const CONFIGURATION_TOP = 44;
const CONFIGURATION_LINE = 4.6;
const configurationRows = (sheet: PrintSheet) => Math.floor((sheet.pageHeight - 20 - CONFIGURATION_TOP) / CONFIGURATION_LINE);

function formatDefinitionValue(definition: OptionDefinition | undefined, value: OptionValue): string {
  return formatOptionValue(definition, value);
}

/** The bill of parts, then every value the draft was made from: body figure, measurements by group, then options. */
function configurationLines(specification: PatternSpecification, project: PatternProject, parts: Part[], garment?: string): ConfigurationLine[] {
  const lines: ConfigurationLine[] = [
    { kind: "heading", text: `${garment ? `${garment} · bill` : "Bill"} of parts · ${parts.length} pieces, ${parts.reduce((total, part) => total + part.block.cutQuantity, 0)} to cut` },
    { kind: "partsHeader" },
    ...parts.map((part): ConfigurationLine => ({ kind: "part", part })),
    { kind: "heading", text: garment ? `${garment} · pattern` : "Pattern" },
    { kind: "value", label: "Specification", value: `${specification.pattern.id} v${specification.pattern.version}` },
    { kind: "value", label: "Project", value: project.name },
    { kind: "value", label: "Made for", value: project.madeFor },
    { kind: "value", label: "Bespoke inlay", value: project.includeInlay ? "Included" : "Not included" },
    { kind: "heading", text: "Figure" },
    ...Object.entries(project.bodyMeasurements).map(([id, value]): ConfigurationLine => ({ kind: "value", label: id.replace(/([A-Z])/g, " $1").toLowerCase(), value: `${value} mm` })),
  ];
  const groups = new Map<string, ConfigurationLine[]>();
  for (const [id, definition] of Object.entries(specification.measurements)) {
    const group = definition.group ?? "Measurements";
    groups.set(group, [...(groups.get(group) ?? []), { kind: "value", label: definition.label, value: formatDefinitionValue(definition, project.measurements[id] ?? definition.default) }]);
  }
  for (const [group, values] of groups) lines.push({ kind: "heading", text: group }, ...values);
  // Options the pattern fixes (hidden) are not part of the user's configuration.
  const options = Object.entries(specification.options).filter(([, definition]) => !definition.hidden);
  if (options.length > 0) lines.push({ kind: "heading", text: "Options" }, ...options.map(([id, definition]): ConfigurationLine => ({ kind: "value", label: definition.label, value: formatDefinitionValue(definition, project.options[id] ?? definition.default) })));
  return lines;
}

/** Splits the lines into sheets, never leaving a heading at the foot of a sheet. */
function paginateConfiguration(lines: ConfigurationLine[], rows: number): ConfigurationLine[][] {
  const sheets: ConfigurationLine[][] = [];
  let column: ConfigurationLine[] = [];
  lines.forEach((line, index) => {
    const orphanHeading = line.kind === "heading" && column.length === rows - 1 && index < lines.length - 1;
    if (column.length === rows || orphanHeading) { sheets.push(column); column = []; }
    column.push(line);
  });
  if (column.length > 0) sheets.push(column);
  return sheets;
}

/** Sheet listing the measurements and options this pattern was drafted with, so a printout can be redrafted. */
function PrintConfiguration(props: { sheet: PrintSheet; draft: DraftResult; title: string; lines: ConfigurationLine[]; page: number; total: number }) {
  const right = props.sheet.pageWidth - 20;
  return (
    <article class="paper">
      <svg viewBox={`0 0 ${props.sheet.pageWidth} ${props.sheet.pageHeight}`} width={`${props.sheet.pageWidth}mm`} height={`${props.sheet.pageHeight}mm`} aria-label={`Pattern configuration sheet ${props.page} of ${props.total}`}>
        <text x={20} y={24} font-size="7" font-weight="bold">Configuration</text>
        <text x={20} y={31} font-size="3.5">{props.title} · Made for {props.draft.madeFor} · Printed {props.draft.printedDate}{props.total > 1 ? ` · ${props.page}/${props.total}` : ""}</text>
        <For each={props.lines}>{(line, row) => {
          const y = CONFIGURATION_TOP + row() * CONFIGURATION_LINE;
          if (line.kind === "partsHeader") return <g font-size="2.7" font-weight="bold" fill="#555"><text x={22} y={y}>Piece</text><text x={PARTS_COLUMNS.cut} y={y} text-anchor="end">Cut</text><text x={PARTS_COLUMNS.grain} y={y}>Grain</text><text x={PARTS_COLUMNS.size} y={y}>Size (W × H)</text><text x={PARTS_COLUMNS.sheets} y={y}>On sheets</text></g>;
          if (line.kind === "part") return <PartRow part={line.part} y={y} right={right} />;
          return line.kind === "heading"
            ? <text x={20} y={y} font-size="3.4" font-weight="bold">{line.text}</text>
            : <g font-size="3.1"><text x={22} y={y}>{line.label}</text><text x={right} y={y} text-anchor="end" font-family="ui-monospace, monospace">{line.value}</text><line x1={22} y1={y + 1.2} x2={right} y2={y + 1.2} stroke="#e4e4e4" stroke-width="0.15" /></g>;
        }}</For>
      </svg>
    </article>
  );
}

const PARTS_COLUMNS = { cut: 102, grain: 105, size: 124, sheets: 150 };

/** Sheet numbers as runs: 1, 2, 3, 5 → "1–3, 5". */
function sheetRanges(sheets: number[]): string {
  const runs: string[] = [];
  for (let i = 0; i < sheets.length; i++) {
    let j = i;
    while (j + 1 < sheets.length && sheets[j + 1] === sheets[j] + 1) j++;
    runs.push(j > i ? `${sheets[i]}–${sheets[j]}` : String(sheets[i]));
    i = j;
  }
  return runs.join(", ") || "—";
}

const truncate = (text: string, length: number) => text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text;

function PartRow(props: { part: Part; y: number; right: number }) {
  const block = props.part.block;
  return <g font-size="2.9">
    <PieceBadge index={props.part.index} code={props.part.code} x={24} y={props.y - 1.1} radius={1.9} />
    <text x={props.part.code ? 29 : 28} y={props.y} font-weight="bold" fill={piecePrintColour(props.part.index)}>{truncate(block.label, props.part.code ? 36 : 38)}</text>
    <text x={PARTS_COLUMNS.cut} y={props.y} text-anchor="end" font-family="ui-monospace, monospace">×{block.cutQuantity}</text>
    <text x={PARTS_COLUMNS.grain} y={props.y}>{block.grainDirection}</text>
    <text x={PARTS_COLUMNS.size} y={props.y} font-family="ui-monospace, monospace">{Math.round(block.bounds.width)} × {Math.round(block.bounds.height)} mm</text>
    <text x={PARTS_COLUMNS.sheets} y={props.y} font-family="ui-monospace, monospace">{truncate(sheetRanges(props.part.sheets), 24)}</text>
    <line x1={22} y1={props.y + 1.2} x2={props.right} y2={props.y + 1.2} stroke="#e4e4e4" stroke-width="0.15" />
  </g>;
}

/** A piece's letter in a disc of its colour, the same on the cover, the bill of parts and the pattern sheets. */
function PieceBadge(props: { index: number; code?: string; x: number; y: number; radius: number }) {
  const letter = () => partLetter(props.index, props.code);
  return <g>
    <circle cx={props.x} cy={props.y} r={props.radius} fill={piecePrintColour(props.index)} stroke="#fff" stroke-width={props.radius * 0.12} />
    <text x={props.x} y={props.y} text-anchor="middle" dominant-baseline="central" font-size={String(props.radius * (letter().length > 2 ? 0.7 : letter().length > 1 ? 0.95 : 1.25))} font-weight="bold" fill="#fff">{letter()}</text>
  </g>;
}

export function PrintPiece(props: { piece: ChapterPiece; hatchId: string; inlayHatchId: string }) {
  return <PrintPieceDrawing draft={props.piece.draft} block={props.piece.block} index={props.piece.index} code={props.piece.code} transform={props.piece.transform} mirrored={props.piece.mirrored} hatchId={props.hatchId} inlayHatchId={props.inlayHatchId} />;
}

function PrintPieceDrawing(props: { draft: DraftResult; block: DraftBlock; index: number; code?: string; transform: string; mirrored?: boolean; hatchId: string; inlayHatchId: string }) {
  const centreX = props.block.bounds.minX + props.block.bounds.width / 2;
  const centreY = props.block.bounds.minY + props.block.bounds.height / 2;
  const grainLength = Math.min(90, Math.max(45, props.block.bounds.height * 0.22));
  const grain = grainEnds(props.block, centreX, centreY, grainLength);
  return (
    <g transform={props.transform}>
      <For each={props.draft.seamAllowances.filter((item) => props.block.seamAllowanceIds.includes(item.id))}>
        {(allowance) => <polygon points={allowance.polygon.map(([x, y]) => `${x},${y}`).join(" ")} fill={`url(#${props.hatchId})`} stroke="#111" stroke-width="0.5" stroke-linejoin="round" />}
      </For>
      <For each={props.draft.inlays.filter((item) => props.block.inlayIds.includes(item.id))}>
        {(inlay) => <g>
          <polygon points={inlay.polygon.map(([x, y]) => `${x},${y}`).join(" ")} fill={`url(#${props.inlayHatchId})`} stroke="#111" stroke-width="0.5" stroke-dasharray="3 1.5" stroke-linejoin="round" />
          <text x={inlay.labelAt[0]} y={inlay.labelAt[1] + 1} text-anchor="middle" font-size="2.6" fill="#333">inlay {Math.round(inlay.distance)}</text>
        </g>}
      </For>
      <For each={props.draft.edges.filter((item) => props.block.internalEdgeIds.includes(item.id))}>
        {(edge) => <path d={edge.segments.map(segmentPath).join(" ")} fill="none" stroke="#333" stroke-width="0.4" stroke-dasharray="3 2" />}
      </For>
      <For each={props.draft.edges.filter((item) => props.block.edgeIds.includes(item.id))}>
        {(edge) => <path d={edge.segments.map(segmentPath).join(" ")} fill="none" stroke="#111" stroke-width="0.5" />}
      </For>
      <For each={props.draft.marks.filter((item) => props.block.markIds.includes(item.id))}>{(mark) => mark.operation === "distributeButtons"
        ? <For each={mark.points}>{([x, y]) => <circle cx={x} cy={y} r="2" fill="none" stroke="#111" stroke-width="0.35" />}</For>
        : mark.operation === "button"
          ? <g fill="none" stroke="#111" stroke-width="0.35"><circle cx={mark.points[0][0]} cy={mark.points[0][1]} r="3" /><circle cx={mark.points[0][0]} cy={mark.points[0][1]} r="1.2" /></g>
        : mark.operation === "buttonhole"
          ? <path d={keyholePath(mark.points[0], mark.points[1], 1.5)} fill="none" stroke="#111" stroke-width="0.35" />
        : mark.operation === "notch"
          ? <path d={`M ${mark.points[0][0] - 4} ${mark.points[0][1] - 3} L ${mark.points[0][0]} ${mark.points[0][1]} L ${mark.points[0][0] - 4} ${mark.points[0][1] + 3}`} fill="none" stroke="#111" stroke-width="0.5" />
          : <line x1={mark.points[0][0]} y1={mark.points[0][1]} x2={mark.points[1][0]} y2={mark.points[1][1]} stroke="#555" stroke-width="0.35" stroke-dasharray="4 3" />}
      </For>

      {/* A piece laid face down flips its labels back about their centre, so they still read. */}
      <g transform={props.mirrored ? `translate(${centreX} ${centreY}) scale(1 -1) translate(${-centreX} ${-centreY})` : undefined}>
      <PieceBadge index={props.index} code={props.code} x={centreX} y={centreY - 33} radius={7} />
      <text x={centreX} y={centreY - 21} text-anchor="middle" font-size="4" font-weight="bold" fill={piecePrintColour(props.index)}>{partLetter(props.index, props.code)} · {props.block.label}</text>
      <text x={centreX} y={centreY - 15} text-anchor="middle" font-size="3.5">{Math.round(props.block.bounds.width)} mm W × {Math.round(props.block.bounds.height)} mm H</text>
      <text x={centreX} y={centreY - 9} text-anchor="middle" font-size="3.5">Cut {props.block.cutQuantity} · Grain: {props.block.grainDirection}</text>
      <text x={centreX} y={centreY - 3} text-anchor="middle" font-size="3.5">Made for: {props.draft.madeFor}</text>
      <text x={centreX} y={centreY + 3} text-anchor="middle" font-size="3.5">Printed: {props.draft.printedDate} · Pattern v{props.draft.patternVersion}</text>
      </g>
      <path d={doubleArrowPath(grain[0], grain[1])} fill="none" stroke="#111" stroke-width="0.45" />
    </g>
  );
}

/** A keyhole buttonhole: a round eye at `eye` and the slit running on to `end`. */
function keyholePath(eye: [number, number], end: [number, number], radius: number): string {
  const length = Math.hypot(end[0] - eye[0], end[1] - eye[1]) || 1;
  const [ux, uy] = [(end[0] - eye[0]) / length, (end[1] - eye[1]) / length];
  const slitStart = [eye[0] + ux * radius, eye[1] + uy * radius];
  return `M ${eye[0] + radius} ${eye[1]} A ${radius} ${radius} 0 1 0 ${eye[0] - radius} ${eye[1]} A ${radius} ${radius} 0 1 0 ${eye[0] + radius} ${eye[1]} M ${slitStart[0]} ${slitStart[1]} L ${end[0]} ${end[1]} M ${end[0] - uy * radius} ${end[1] + ux * radius} L ${end[0] + uy * radius} ${end[1] - ux * radius}`;
}

/** Places a piece on the print drawing: turned for printing (labels and grain arrow turn with it), then moved. */
function printTransform(block: DraftBlock): string {
  return `translate(${block.printOffset[0]} ${block.printOffset[1]}) rotate(${block.printRotation})`;
}

/**
 * The 10 mm grid, in drawing coordinates: sheets advance by whole grid squares,
 * so each line (and each heavier 50 mm line) carries on across the overlap onto
 * the next sheet.
 */
function PrintGrid(props: { sheet: PrintSheet; originX: number; originY: number }) {
  const { marginX, marginY, width, height } = props.sheet;
  const lines = (length: number) => Array.from({ length: Math.floor(length / PRINT_GRID) + 1 }, (_, index) => index * PRINT_GRID);
  const major = (at: number) => at % 50 === 0;
  return <g class="alignment-grid">
    <For each={lines(width)}>{(x) => <line x1={marginX + x} y1={marginY} x2={marginX + x} y2={marginY + height} stroke={major(props.originX + x) ? "#b5b5b5" : "#dddddd"} stroke-width={major(props.originX + x) ? "0.25" : "0.12"} />}</For>
    <For each={lines(height)}>{(y) => <line x1={marginX} y1={marginY + y} x2={marginX + width} y2={marginY + y} stroke={major(props.originY + y) ? "#b5b5b5" : "#dddddd"} stroke-width={major(props.originY + y) ? "0.25" : "0.12"} />}</For>
  </g>;
}

/** Where the next sheet's edge lies: the overlap strip down the right and along the bottom. */
function OverlapLines(props: { sheet: PrintSheet }) {
  const { marginX, marginY, width, height, overlap } = props.sheet;
  const right = marginX + width - overlap;
  const bottom = marginY + height - overlap;
  return <g fill="none" stroke="#555" stroke-width="0.3" stroke-dasharray="2 1.2">
    <line x1={right} y1={marginY} x2={right} y2={marginY + height} />
    <line x1={marginX} y1={bottom} x2={marginX + width} y2={bottom} />
  </g>;
}

/**
 * This sheet's share of the rainbow alignment lines, with each line's number
 * (vertical) or letter (horizontal) at both ends so neighbouring sheets can be
 * matched by colour and label.
 */
function AlignmentLines(props: { sheet: PrintSheet; originX: number; originY: number }) {
  const { marginX: left, marginY: top, width, height } = props.sheet;
  const label = { "font-size": "3", "font-weight": "bold", stroke: "#fff", "stroke-width": "0.9", "paint-order": "stroke", "text-anchor": "middle" } as const;
  return <g class="rainbow-alignment">
    <For each={alignmentLines(props.originX, width)}>{(line) => {
      const x = left + line.at - props.originX;
      return <g fill={alignmentColour(line.index)}>
        <line x1={x} y1={top} x2={x} y2={top + height} stroke={alignmentColour(line.index)} stroke-width="0.4" stroke-dasharray="6 1.5" opacity="0.85" />
        <text x={x} y={top + 4.5} {...label}>{line.index + 1}</text>
        <text x={x} y={top + height - 2} {...label}>{line.index + 1}</text>
      </g>;
    }}</For>
    <For each={alignmentLines(props.originY, height)}>{(line) => {
      const y = top + line.at - props.originY;
      return <g fill={alignmentColour(line.index + 3)}>
        <line x1={left} y1={y} x2={left + width} y2={y} stroke={alignmentColour(line.index + 3)} stroke-width="0.4" stroke-dasharray="6 1.5" opacity="0.85" />
        <text x={left + 3.5} y={y - 1.2} {...label}>{alignmentLetter(line.index)}</text>
        <text x={left + width - 3.5} y={y - 1.2} {...label}>{alignmentLetter(line.index)}</text>
      </g>;
    }}</For>
  </g>;
}

/**
 * Crosses on the left and top edges, and on the right and bottom overlap lines,
 * all on whole grid squares: each sheet's edge crosses sit exactly on its
 * neighbour's overlap-line crosses.
 */
function RegistrationMarks(props: { sheet: PrintSheet }) {
  const { marginX, marginY, width, height, overlap } = props.sheet;
  const midX = marginX + Math.round((width - overlap) / 2 / PRINT_GRID) * PRINT_GRID;
  const midY = marginY + Math.round((height - overlap) / 2 / PRINT_GRID) * PRINT_GRID;
  const points: [number, number][] = [[marginX, midY], [marginX + width - overlap, midY], [midX, marginY], [midX, marginY + height - overlap]];
  return <g><For each={points}>{([x, y]) => <path d={`M ${x - 3} ${y} H ${x + 3} M ${x} ${y - 3} V ${y + 3} M ${x - 1.5} ${y - 1.5} L ${x + 1.5} ${y + 1.5} M ${x + 1.5} ${y - 1.5} L ${x - 1.5} ${y + 1.5}`} fill="none" stroke="#111" stroke-width="0.3" />}</For></g>;
}

function grainEnds(block: DraftBlock, x: number, y: number, length: number): [[number, number], [number, number]] {
  if (block.grainDirection === "horizontal") return [[x - length / 2, y + 8], [x + length / 2, y + 8]];
  if (block.grainDirection === "bias") return [[x - length * 0.35, y + 8 + length * 0.35], [x + length * 0.35, y + 8 - length * 0.35]];
  return [[x, y + 8 + length / 2], [x, y + 8 - length / 2]];
}

function doubleArrowPath(from: [number, number], to: [number, number]): string {
  const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
  const heads = [[from, angle], [to, angle + Math.PI]] as const;
  return [`M ${from[0]} ${from[1]} L ${to[0]} ${to[1]}`, ...heads.map(([point, direction]) => `M ${point[0] + Math.cos(direction - 0.55) * 4} ${point[1] + Math.sin(direction - 0.55) * 4} L ${point[0]} ${point[1]} L ${point[0] + Math.cos(direction + 0.55) * 4} ${point[1] + Math.sin(direction + 0.55) * 4}`)].join(" ");
}

export function segmentPath(segment: DraftSegment): string {
  if (segment.kind === "line") return `M ${segment.start[0]} ${segment.start[1]} L ${segment.end[0]} ${segment.end[1]}`;
  return `M ${segment.start[0]} ${segment.start[1]} C ${segment.control1[0]} ${segment.control1[1]}, ${segment.control2[0]} ${segment.control2[1]}, ${segment.end[0]} ${segment.end[1]}`;
}
